import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReportFormData } from "../../types/ReportFormData";
import { ReportRequest } from "../../types/ReportRequest";
import { ReportTemplate } from "../../types/ReportTemplate";
import { ScreenGroup } from "../../types/ScreenGroup";
import { Monitor } from "../../types/Screen";
import { Media } from "../../types/Media";
import { Holiday } from "../../types/Holiday";
import { DEFAULT_SELECTED_DAYS } from "../../utils/constants";
import { formatDateToISO, validateDateRange } from "../../utils/dateHelpers";
import { isFormDataEqual } from "../../utils/formDataHelpers";
import {
	generateReport,
	getScreens,
	getMedia,
	getHolidays,
} from "../../services/reportService";
import { loadTemplate, getAllTemplates } from "../../utils/templateStorage";
import GeneralParamsSection from "./GeneralParamsSection";
import AdvancedFilterSection from "./AdvancedFilterSection";
import TemplateDropdown from "./TemplateDropdown";
import SaveTemplateModal from "./SaveTemplateModal";
import Button from "../ui/Button";
import { getTranslatedError } from "../../utils/translateError";

interface ReportFormProps {
	onSuccess?: () => void;
}

const ReportForm: React.FC<ReportFormProps> = ({ onSuccess }) => {
	const { t } = useTranslation();
	const [formData, setFormData] = useState<ReportFormData>({
		report_title: "",
		client_name: "",
		campaign_name: "",
		included_media_IDs: [],
		excluded_screen_IDs: [],
		start_date: null,
		end_date: null,
		days: [...DEFAULT_SELECTED_DAYS],
		exclude_holidays: false,
		specific_dates_to_exclude: [],
		daily_working_hours: [],
		offline_logs: [],
		screen_renames: [],
		screen_groups: [],
	});

	const [errors, setErrors] = useState<
		Partial<Record<keyof ReportFormData, string>>
	>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(
		null,
	);
	const [currentTemplateData, setCurrentTemplateData] =
		useState<ReportFormData | null>(null);
	const [wasTemplateUsed, setWasTemplateUsed] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [screenOptions, setScreenOptions] = useState<Monitor[]>([]);
	const [mediaOptions, setMediaOptions] = useState<Media[]>([]);
	const [templates, setTemplates] = useState<ReportTemplate[]>([]);
	const [holidays, setHolidays] = useState<Holiday[]>([]);
	const [loadingScreens, setLoadingScreens] = useState(false);
	const [loadingMedia, setLoadingMedia] = useState(false);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [loadingHolidays, setLoadingHolidays] = useState(false);
	const [holidaysError, setHolidaysError] = useState<string | null>(null);

	const initialLoadFired = useRef(false);

	// Fetch screens, media, and templates once on mount
	useEffect(() => {
		if (initialLoadFired.current) return;
		initialLoadFired.current = true;

		const load = async () => {
			setLoadingScreens(true);
			setLoadingMedia(true);
			setLoadingTemplates(true);
			try {
				const [screens, media, tpls] = await Promise.all([
					getScreens(),
					getMedia(),
					getAllTemplates(),
				]);
				setScreenOptions(screens);
				setMediaOptions(media);
				setTemplates(tpls);
			} catch (err) {
				setScreenOptions([]);
				setMediaOptions([]);
				setTemplates([]);
				console.error("Failed to load initial data:", err);
			} finally {
				setLoadingScreens(false);
				setLoadingMedia(false);
				setLoadingTemplates(false);
			}
		};
		load();
	}, []);

	useEffect(() => {
		if (
			!formData.start_date ||
			!formData.end_date ||
			!validateDateRange(formData.start_date, formData.end_date)
		) {
			setHolidays([]);
			setHolidaysError(null);
			setLoadingHolidays(false);
			return;
		}

		let isCancelled = false;
		const startDate = formatDateToISO(formData.start_date);
		const endDate = formatDateToISO(formData.end_date);

		const loadHolidays = async () => {
			setLoadingHolidays(true);
			setHolidaysError(null);
			try {
				const fetchedHolidays = await getHolidays(startDate, endDate);
				if (!isCancelled) {
					setHolidays(fetchedHolidays);
				}
			} catch (err) {
				if (!isCancelled) {
					setHolidays([]);
					setHolidaysError(
						getTranslatedError(err, "errors.loadHolidaysFailed"),
					);
				}
			} finally {
				if (!isCancelled) {
					setLoadingHolidays(false);
				}
			}
		};

		loadHolidays();

		return () => {
			isCancelled = true;
		};
	}, [formData.start_date, formData.end_date]);

	const refreshTemplates = async () => {
		try {
			const tpls = await getAllTemplates();
			setTemplates(tpls);
		} catch (err) {
			console.error("Failed to refresh templates:", err);
		}
	};

	const handleChange = (data: Partial<ReportFormData>) => {
		setFormData((prev) => ({ ...prev, ...data }));
		// Clear errors for changed fields
		const clearedErrors = { ...errors };
		Object.keys(data).forEach((key) => {
			delete clearedErrors[key as keyof ReportFormData];
		});
		setErrors(clearedErrors);
		setSubmitError(null);
		// Clear template ID and data when form is manually modified
		if (currentTemplateId) {
			setCurrentTemplateId(null);
			setCurrentTemplateData(null);
		}
	};

	const [screenGroupWarning, setScreenGroupWarning] = useState<string | null>(
		null,
	);
	const prevExcludedRef = useRef<number[]>(formData.excluded_screen_IDs);

	// Clean up renames/groups when excluded screens change
	useEffect(() => {
		const prev = prevExcludedRef.current;
		const curr = formData.excluded_screen_IDs;
		prevExcludedRef.current = curr;

		if (JSON.stringify(prev) === JSON.stringify(curr)) return;

		const excludedSet = new Set(curr);
		let modified = false;

		const cleanedRenames = formData.screen_renames.filter((entry) => {
			const dominated = entry.screen_ids.some((id) => excludedSet.has(id));
			if (dominated) modified = true;
			return !dominated;
		});

		const cleanedGroups: ScreenGroup[] = [];
		for (const group of formData.screen_groups) {
			const remaining = group.screen_ids.filter((id) => !excludedSet.has(id));
			if (remaining.length !== group.screen_ids.length) modified = true;
			if (remaining.length >= 2) {
				cleanedGroups.push({ ...group, screen_ids: remaining });
			} else if (remaining.length < group.screen_ids.length) {
				// Group dropped below 2 screens — remove entirely
			} else {
				cleanedGroups.push(group);
			}
		}

		if (modified) {
			setFormData((prev) => ({
				...prev,
				screen_renames: cleanedRenames,
				screen_groups: cleanedGroups,
			}));
			setScreenGroupWarning(t("form.warnings.screenGroupRemoved"));
			setTimeout(() => setScreenGroupWarning(null), 5000);
		}
	}, [
		formData.excluded_screen_IDs,
		formData.screen_renames,
		formData.screen_groups,
		t,
	]);

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof ReportFormData, string>> = {};

		if (!formData.report_title.trim()) {
			newErrors.report_title = t("form.validation.reportTitleRequired");
		}

		if (!formData.client_name.trim()) {
			newErrors.client_name = t("form.validation.clientNameRequired");
		}

		if (!formData.campaign_name.trim()) {
			newErrors.campaign_name = t("form.validation.campaignNameRequired");
		}

		if (!formData.start_date) {
			newErrors.start_date = t("form.validation.startDateRequired");
		}

		if (!formData.end_date) {
			newErrors.end_date = t("form.validation.endDateRequired");
		}

		if (formData.start_date && formData.end_date) {
			if (!validateDateRange(formData.start_date, formData.end_date)) {
				newErrors.end_date = t("form.validation.endDateAfterStart");
			}
		}

		if (formData.days.length === 0) {
			newErrors.days = t("form.validation.daysRequired");
		}

		if (formData.included_media_IDs.length === 0) {
			newErrors.included_media_IDs = t("form.validation.mediaRequired");
		}

		// Validate screen renames
		for (let i = 0; i < formData.screen_renames.length; i++) {
			const entry = formData.screen_renames[i];
			if (!entry.name.trim()) {
				newErrors.screen_renames =
					newErrors.screen_renames ||
					t("form.validation.renameNameRequired");
			}
		}

		// Validate screen groups
		for (let i = 0; i < formData.screen_groups.length; i++) {
			const group = formData.screen_groups[i];
			if (!group.name.trim()) {
				newErrors.screen_groups =
					newErrors.screen_groups ||
					t("form.validation.groupNameRequired");
			}
			if (group.screen_ids.length < 2) {
				newErrors.screen_groups =
					newErrors.screen_groups ||
					t("form.validation.groupMinScreens");
			}
		}

		// Check for duplicate screen assignments across renames and groups
		const allAssignedIds = new Set<number>();
		let hasDuplicate = false;
		const allEntries = [...formData.screen_renames, ...formData.screen_groups];
		for (const entry of allEntries) {
			for (const id of entry.screen_ids) {
				if (allAssignedIds.has(id)) hasDuplicate = true;
				allAssignedIds.add(id);
			}
		}
		if (hasDuplicate) {
			newErrors.screen_groups = t("form.validation.duplicateScreen");
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const convertToReportRequest = (data: ReportFormData): ReportRequest => {
		const merged = [...data.screen_renames, ...data.screen_groups];
		return {
			report_title: data.report_title,
			client_name: data.client_name,
			campaign_name: data.campaign_name,
			included_media_IDs: data.included_media_IDs,
			excluded_screen_IDs: data.excluded_screen_IDs,
			start_date: formatDateToISO(data.start_date),
			end_date: formatDateToISO(data.end_date),
			days: data.days,
			exclude_holidays: data.exclude_holidays,
			specific_dates_to_exclude:
				data.specific_dates_to_exclude.map(formatDateToISO),
			daily_working_hours: data.daily_working_hours,
			offline_logs: data.offline_logs,
			screen_groups: merged.length > 0 ? merged : undefined,
		};
	};

	const handleLoadTemplate = async (template: ReportTemplate) => {
		// Update lastUsedAt timestamp
		try {
			await loadTemplate(template.id);
		} catch (error) {
			console.error("Failed to update template lastUsedAt:", error);
		}
		setFormData(template.data);
		setCurrentTemplateId(template.id);
		setCurrentTemplateData(template.data);
		setWasTemplateUsed(true);
		setErrors({});
		setSubmitError(null);
	};

	const handleClearTemplate = () => {
		setCurrentTemplateId(null);
		setCurrentTemplateData(null);
	};

	// Check if current form data matches the loaded template
	const isSaveDisabled =
		currentTemplateId !== null &&
		currentTemplateData !== null &&
		isFormDataEqual(formData, currentTemplateData);

	const handleSaveTemplate = () => {
		refreshTemplates();
		setShowSaveModal(false);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitError(null);

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			const reportRequest = convertToReportRequest(formData);
			const blob = await generateReport(reportRequest);

			// Trigger PDF download
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${formData.report_title || "report"}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			// Show save modal if no template was used
			if (!wasTemplateUsed) {
				setShowSaveModal(true);
			}

			// Call success callback if provided
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			setSubmitError(getTranslatedError(err, "errors.generateReportFailed"));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-8">
			{/* Template Dropdown */}
			<div className="pb-6 border-b border-gray-200">
				<TemplateDropdown
					currentTemplateId={currentTemplateId}
					onLoadTemplate={handleLoadTemplate}
					onClearTemplate={handleClearTemplate}
					templates={templates}
					loadingTemplates={loadingTemplates}
					onRefreshTemplates={refreshTemplates}
				/>
			</div>

			{/* General Parameters Section */}
			<GeneralParamsSection
				formData={formData}
				onChange={handleChange}
				errors={errors}
				screenOptions={screenOptions}
				loadingScreens={loadingScreens}
				mediaOptions={mediaOptions}
				loadingMedia={loadingMedia}
			/>

			{/* Advanced Filtering Section */}
			<AdvancedFilterSection
				formData={formData}
				onChange={handleChange}
				errors={errors}
				screenOptions={screenOptions}
				loadingScreens={loadingScreens}
				holidays={holidays}
				loadingHolidays={loadingHolidays}
				holidaysError={holidaysError}
			/>

			{/* Screen Group Warning */}
			{screenGroupWarning && (
				<div className="p-4 bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-amber-500 text-amber-700 rounded-lg shadow-sm">
					<div className="flex items-center">
						<svg
							className="w-5 h-5 mr-2 flex-shrink-0"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path
								fillRule="evenodd"
								d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
								clipRule="evenodd"
							/>
						</svg>
						<span className="font-semibold">{screenGroupWarning}</span>
					</div>
				</div>
			)}

			{/* Submit Error */}
			{submitError && (
				<div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 rounded-lg shadow-sm">
					<div className="flex items-center">
						<svg
							className="w-5 h-5 mr-2 flex-shrink-0"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path
								fillRule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clipRule="evenodd"
							/>
						</svg>
						<span className="font-semibold">{submitError}</span>
					</div>
				</div>
			)}

			{/* Submit Buttons */}
			<div className="flex justify-end gap-3 pt-6 mt-6">
				<Button
					type="button"
					variant="secondary"
					size="lg"
					onClick={() => setShowSaveModal(true)}
					disabled={isSubmitting || isSaveDisabled}
				>
					{t("form.saveTemplateButton")}
				</Button>
				<Button
					type="submit"
					variant="primary"
					size="lg"
					disabled={isSubmitting}
				>
					{isSubmitting ? (
						<span className="flex items-center">
							<svg
								className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								></circle>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								></path>
							</svg>
							{t("form.generatingReport")}
						</span>
					) : (
						t("form.generateReport")
					)}
				</Button>
			</div>

			{/* Save Template Modal */}
			<SaveTemplateModal
				isOpen={showSaveModal}
				onClose={() => setShowSaveModal(false)}
				formData={formData}
				onSave={handleSaveTemplate}
			/>
		</form>
	);
};

export default ReportForm;
